/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useCallback, useMemo } from "react";
import { saveAs } from "file-saver";
import { Typography, Button, Dialog } from "@material-ui/core";
import PublishOutlined from "@material-ui/icons/PublishOutlined";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { FileUploader } from "../../../../components//CSVUploading/FileUploader";
import { useExternalContacts } from "../../context/externalContacts";
import { ExternalContactsPreview } from ".";

const modalStates = {
	UPLOAD: "UPLOAD",
	DISPLAY_RESULTS: "DISPLAY_RESULTS",
};

const downloadCsvTemplate = () => {
	const header = "fullName,phone,tags,azureAdUserId";
	const sampleRow = `John Smith,+14155552671,"tag1,tag2",optional-azure-user-id`;

	const blob = new Blob([`${header}\n${sampleRow}`], { type: "text/csv;charset=utf-8" });
	saveAs(blob, "external-contacts-template.csv");
};

/**
 * Displays a dialog where a user can upload a CSV file of contacts, preview the changes,
 * review any errors, and save the contacts.
 */
export const ExternalContactsCsvUpload = () => {
	const { createContacts, parseContactsCsv, showSnackbar } = useExternalContacts();

	const [fileName, setFileName] = useState("");
	const [open, setOpen] = useState(false);
	const [parsedContacts, setParsedContacts] = useState([]);
	const [errors, setErrors] = useState([]);
	const [modalState, setModalState] = useState(modalStates.UPLOAD);
	const [isSaving, setIsSaving] = useState(false);

	const resetState = useCallback(() => {
		setFileName("");
		setParsedContacts([]);
		setErrors([]);
		setModalState(modalStates.UPLOAD);
		setIsSaving(false);
	}, []);

	const parseCsv = useCallback(
		(csvData, name) => {
			if (!name.endsWith(".csv")) {
				showSnackbar("Only csv files are supported.", "error");
			} else {
				const parseResults = parseContactsCsv(csvData);
				setErrors(parseResults.parseErrors);
				setParsedContacts(parseResults.results);
				setModalState(modalStates.DISPLAY_RESULTS);
			}
		},
		[parseContactsCsv, showSnackbar],
	);

	const createContactsHandler = useCallback(async () => {
		try {
			setIsSaving(true);
			const contactsToSave = parsedContacts.map(({ azureAdUserId, contactId, fullName, phone, tags }) => ({
				azureAdUserId,
				contactId,
				fullName,
				phone,
				tags,
			}));

			await createContacts(contactsToSave);
			setOpen(false);
			resetState();
		} catch (_) {}

		setIsSaving(false);
	}, [parsedContacts, createContacts, resetState]);

	const isDataValid = useMemo(() => {
		if (errors?.length > 0) {
			return false;
		}

		const invalidContactExists = parsedContacts.some(({ isValid }) => !isValid);

		if (parsedContacts?.length < 1 || invalidContactExists) {
			return false;
		}

		return true;
	}, [parsedContacts, errors]);

	return (
		<div>
			<Button variant="outlined" size="small" onClick={() => setOpen(true)} startIcon={<PublishOutlined />}>
				Import
			</Button>

			<Dialog
				fullWidth
				maxWidth="md"
				open={open}
				onClose={() => {
					setOpen(false);
					setFileName("");
				}}
			>
				<AppBar color="default" elevation={2} position="sticky" style={{ padding: 0 }}>
					<Toolbar style={{ justifyContent: "space-between" }}>
						<div style={{ width: 145 }}>
							<IconButton
								edge="start"
								color="inherit"
								style={{ float: "left" }}
								size="small"
								onClick={() => {
									setOpen(false);
									resetState();
								}}
								aria-label="close"
							>
								<CloseIcon />
							</IconButton>
						</div>
						<Typography variant="h6">Import contacts from CSV</Typography>
						<div style={{ minWidth: 150 }}>
							<Button
								size="small"
								data-testid="process-file-btn"
								variant="contained"
								color="secondary"
								disabled={!isDataValid || isSaving}
								onClick={createContactsHandler}
							>
								Import Contact(s)
							</Button>
						</div>
					</Toolbar>
				</AppBar>
				<div style={{ padding: "0 30px", margin: "20px 0 20px 0" }}>
					{modalState === modalStates.UPLOAD && (
						<>
							<div style={{ margin: "10px 0" }}>
								<span className="text-muted" style={{ display: "inline-block" }}>
									To get started with importing contacts, you can{" "}
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											downloadCsvTemplate();
											return false;
										}}
									>
										click here to download a CSV template
									</a>{" "}
									with a sample row and use it to populate your contact information. Once you have your CSV ready, drag
									it into the designated area below or click below to show a file picker dialog. When the file is
									processed, you can preview the list of contacts along with any validation errors.
								</span>
							</div>
							<FileUploader
								minHeight="250px"
								width="100%"
								fileName={fileName}
								setFile={parseCsv}
								setFileName={setFileName}
							></FileUploader>
						</>
					)}
					{modalState === modalStates.DISPLAY_RESULTS && (
						<div>
							{!isDataValid && (
								<>
									<div className="text-muted mb-4" style={{ display: "inline-block" }}>
										Errors were found while processing the CSV. Please review the errors and contact validation messages
										below. When you are ready to upload another file press the 'RESET UPLOAD' button.
									</div>
									{errors?.length > 0 && (
										<>
											<div className="mb-2">File processing errors:</div>
											{errors?.map((e, idx) => (
												<div
													key={idx}
													style={{
														color: "#d10000",
														fontSize: "12px",
													}}
												>
													{e}
												</div>
											))}
										</>
									)}
								</>
							)}
							<div style={{ margin: "0 0 20px 0" }}>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										margin: "10px 0 10px 0",
									}}
								>
									<span>Preview contacts:</span>
									<Button size="small" data-testid="process-file-btn" variant="outlined" onClick={resetState}>
										Reset upload
									</Button>
								</div>
								<ExternalContactsPreview parsedContacts={parsedContacts} />
							</div>
						</div>
					)}
				</div>
			</Dialog>
		</div>
	);
};
